// covered-by: frontends/carrie/src/ask-v2/review/__tests__/reviewCommentTags.test.ts
import { type AskPunttReviewComment } from "@mg/schemas/src/christo/askPunttReview";

/** Wire tag ids emitted on review comments (schema lands separately). */
export const REVIEW_COMMENT_TAG_IDS = [
  "nutrition",
  "completeness",
  "copy-diff",
  "code-values",
  "variants",
  "regulated-copy",
] as const;

export type ReviewCommentTagId = (typeof REVIEW_COMMENT_TAG_IDS)[number];

export type CommentWithOptionalTags = AskPunttReviewComment & {
  tags?: readonly string[];
};

const TAG_ALIAS: Record<string, ReviewCommentTagId> = {
  nutrition: "nutrition",
  completeness: "completeness",
  "copy-diff": "copy-diff",
  copy_diff: "copy-diff",
  "code-values": "code-values",
  code_values: "code-values",
  codes_values: "code-values",
  // why: warhol's reviewer and domain name, the one the wire actually carries.
  "codes-values": "code-values",
  variants: "variants",
  "regulated-copy": "regulated-copy",
  regulated_copy: "regulated-copy",
  // why: warhol's deterministic checks tag a finding with the check that raised
  // it, not a domain. Unmapped, every check finding read as untagged, stayed
  // visible under every filter, and the filter could narrow nothing.
  "line-diff": "copy-diff",
  diacritics: "copy-diff",
  typography: "copy-diff",
  correspondence: "copy-diff",
  identifier: "code-values",
  "barcode-zone": "code-values",
  "dangling-field": "completeness",
  "mandatory-copy": "regulated-copy",
  "enumeration-order": "regulated-copy",
  consistency: "variants",
};

/** The prefix warhol writes on a finding an expert's method raised. */
export const EXPERT_TAG_PREFIX = "expert:";

/** One expert's review, as the "raised by" filter keys it: `expert:<userId>`. */
export type ExpertFilterKey = `${typeof EXPERT_TAG_PREFIX}${string}`;

/** The AI findings no expert's method raised: Puntt's own reviewers and checks. */
export const PUNTT_FINDINGS_SOURCE = "puntt";

/** Who raised a finding, as the "raised by" filter toggles it. */
export type ReviewCommentSourceKey =
  | ExpertFilterKey
  | typeof PUNTT_FINDINGS_SOURCE;

/** The "raised by" filter's starting state: nothing hidden. */
export const NO_HIDDEN_SOURCES: ReadonlySet<ReviewCommentSourceKey> =
  new Set<ReviewCommentSourceKey>();

export type ExpertFilterOption = {
  key: ExpertFilterKey;
  expertId: string;
  /** The expert's name when the page knows it; the filter falls back to a generic label. */
  name?: string;
};

/** i18n keys under askV2.review.commentTags.* */
export function reviewCommentTagLabelKey(
  tag: ReviewCommentTagId,
): `askV2.review.commentTags.${ReviewCommentTagId}` {
  return `askV2.review.commentTags.${tag}`;
}

export function normalizeReviewCommentTag(
  value: string,
): ReviewCommentTagId | null {
  const trimmed = value.trim().toLowerCase();
  if (!trimmed) return null;
  return TAG_ALIAS[trimmed] ?? null;
}

export function reviewCommentTags(
  comment: CommentWithOptionalTags,
): ReviewCommentTagId[] {
  const raw = comment.tags ?? [];
  const normalized = raw
    .map((tag) => normalizeReviewCommentTag(tag))
    .filter((tag): tag is ReviewCommentTagId => tag != null);
  return Array.from(new Set(normalized));
}

/** The experts whose methods raised this finding, as filter keys. */
export function reviewCommentExpertKeys(
  comment: CommentWithOptionalTags,
): ExpertFilterKey[] {
  const keys = (comment.tags ?? [])
    .map((tag) => tag.trim().toLowerCase())
    .filter(
      (tag): tag is ExpertFilterKey =>
        tag.startsWith(EXPERT_TAG_PREFIX) &&
        tag.length > EXPERT_TAG_PREFIX.length,
    );
  return Array.from(new Set(keys));
}

/**
 * One filter entry per expert whose method raised a finding in this review,
 * in the order their findings first appear.
 */
export function reviewExpertFilterOptions(
  comments: readonly CommentWithOptionalTags[],
  nameByUserId: Readonly<Record<string, string>> = {},
): ExpertFilterOption[] {
  const seen = new Set<ExpertFilterKey>();
  const options: ExpertFilterOption[] = [];
  for (const comment of comments) {
    if (comment.isDeleted) continue;
    for (const key of reviewCommentExpertKeys(comment)) {
      if (seen.has(key)) continue;
      seen.add(key);
      const expertId = key.slice(EXPERT_TAG_PREFIX.length);
      const name = nameByUserId[expertId]?.trim();
      options.push({ key, expertId, ...(name ? { name } : {}) });
    }
  }
  return options;
}

export function commentMatchesActiveTags(
  comment: CommentWithOptionalTags,
  activeTags: ReadonlySet<ReviewCommentTagId>,
): boolean {
  const tags = reviewCommentTags(comment);
  if (tags.length === 0) return true;
  return tags.some((tag) => activeTags.has(tag));
}

/** Who raised this finding; empty for a comment a person wrote. */
export function reviewCommentSources(
  comment: CommentWithOptionalTags,
): ReviewCommentSourceKey[] {
  const experts = reviewCommentExpertKeys(comment);
  if (experts.length > 0) return experts;
  return comment.isAI ? [PUNTT_FINDINGS_SOURCE] : [];
}

/**
 * Whether the "raised by" filter keeps this finding: hidden only when every
 * source that raised it is hidden.
 *
 * why a hidden set, not an active one: an expert's entry appears only once
 * their findings load, and a new entry must start shown.
 */
export function commentMatchesShownSources(
  comment: CommentWithOptionalTags,
  hiddenSources: ReadonlySet<ReviewCommentSourceKey>,
): boolean {
  const sources = reviewCommentSources(comment);
  if (sources.length === 0) return true;
  return sources.some((source) => !hiddenSources.has(source));
}

export function isReviewCommentVisible(
  comment: CommentWithOptionalTags,
  activeTags: ReadonlySet<ReviewCommentTagId>,
  hiddenSources: ReadonlySet<ReviewCommentSourceKey> = NO_HIDDEN_SOURCES,
): boolean {
  if (comment.isDeleted) return false;
  return (
    commentMatchesActiveTags(comment, activeTags) &&
    commentMatchesShownSources(comment, hiddenSources)
  );
}

export function defaultActiveReviewCommentTags(): Set<ReviewCommentTagId> {
  return new Set(REVIEW_COMMENT_TAG_IDS);
}
